import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { CameraZoneRegionsActions } from '@states/camera-zone-regions/camera-zone-regions.actions';
import { ZoneRegionsActions } from '@states/zone-regions/zone-regions.actions';
import { buildEditorDataFromApiExport } from '@states/camera-zone-regions/camera-zone-regions.feature';
import { ZoneRegionsService } from '../../services/zone-regions.service';
import { ZoneRegionsModels } from '@share-models';

type ZoneRegionEditorData = ZoneRegionsModels.UI.ZoneRegionEditorData;

export const loadForCamera$ = createEffect(
  (
    actions$ = inject(Actions),
    zoneRegionsService = inject(ZoneRegionsService),
  ) =>
    actions$.pipe(
      ofType(CameraZoneRegionsActions.loadForCamera),
      switchMap(({ cameraId, edgeId, locationId }) => {
        return forkJoin({
          byCamera: zoneRegionsService.getZoneRegionsByCamera(cameraId),
          all: zoneRegionsService.getAllZoneRegionsShallow(),
        }).pipe(
          switchMap(({ byCamera, all }) => {
            if (byCamera.length === 0) {
              return of(
                CameraZoneRegionsActions.loadForCameraSuccess({
                  initialZoneRegions: [],
                  allZoneRegions: all ?? [],
                  zoneRegionCameraDocIds: {},
                }),
              );
            }

            return forkJoin(
              byCamera.map((zr) =>
                zoneRegionsService.getZoneRegionCameras(zr._id).pipe(
                  map((cameras) => ({ zoneRegion: zr, cameras })),
                ),
              ),
            ).pipe(
              map((results) => {
                const initialZoneRegions: ZoneRegionEditorData[] = [];
                const zoneRegionCameraDocIds: Record<string, string> = {};
                for (const { zoneRegion: zr, cameras } of results) {
                  const camDoc = cameras.find(
                    (c) =>
                      c.cameraId === cameraId &&
                      c.edgeId === edgeId &&
                      c.locationId === locationId,
                  );
                  if (!camDoc) continue;
                  zoneRegionCameraDocIds[zr._id] = camDoc._id;
                  initialZoneRegions.push(
                    buildEditorDataFromApiExport(zr, camDoc.data ?? []),
                  );
                }
                return CameraZoneRegionsActions.loadForCameraSuccess({
                  initialZoneRegions,
                  allZoneRegions: all ?? [],
                  zoneRegionCameraDocIds,
                });
              }),
            );
          }),
          catchError((err) =>
            of(
              CameraZoneRegionsActions.loadForCameraFail({
                error: err?.message ?? 'Failed to load zone regions',
              }),
            ),
          ),
        );
      }),
    ),
  { functional: true },
);

export const saveWithPayload$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(CameraZoneRegionsActions.saveWithPayload),
      switchMap(({ request }) => [
        CameraZoneRegionsActions.setPendingSave({ request }),
        ZoneRegionsActions.startSaveZoneRegionsWithCameras({ request, toastContext: 'camera-edit' }),
      ]),
    ),
  { functional: true },
);
